import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import { useAuth } from '../../hooks/useAuth';
import { usersService } from '../../services/users';

interface ClaimMembershipDialogProps {
  open: boolean;
  invitationToken: string;
  /**
   * Who is asking: a gym registering a member, a competition entering
   * somebody, or a place inviting somebody onto its staff.
   */
  kind?: 'gym' | 'competition' | 'staff';
  /** The gym — or the competition — that registered this person. */
  gymName: string | null;
  /** The name on their record, so the visitor can tell it is them. */
  memberName: string;
  /** Email the invitation was sent to. */
  invitedEmail: string;
}

/**
 * Shown when somebody opens a gym's or a competition's invitation while
 * already signed in as another account. They either attach the membership
 * (or the entry) to the account they are in, or sign out and register as the
 * invited address.
 *
 * Not dismissible: continuing to the sign-up form underneath while signed in
 * as somebody else would create a second account without meaning to.
 */
export default function ClaimMembershipDialog({
  open,
  invitationToken,
  kind = 'gym',
  gymName,
  memberName,
  invitedEmail,
}: ClaimMembershipDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  const [claiming, setClaiming] = useState(false);

  // The gym's copy stands; the others differ only in who is asking and why.
  const copy = (key: string) => (kind === 'gym' ? `invitation.claim.${key}` : `invitation.claim.${kind}.${key}`);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      await usersService.claimInvitation(invitationToken);
      toast({ title: t(copy('success'), { gym: gymName ?? '' }) });
      window.location.href = '/';
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('auth.unexpectedError'),
        variant: 'destructive',
      });
      setClaiming(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    // Full reload so the page comes back with the sign-up form and no session.
    window.location.reload();
  };

  return (
    <Dialog open={open}>
      {/* The close button is hidden: dropping through to the sign-up form while
          signed in as somebody else would create a second account by accident. */}
      <DialogContent className="sm:max-w-md [&>button]:hidden">
        <DialogHeader>
          <DialogTitle>{t(copy('title'), { gym: gymName ?? '' })}</DialogTitle>
          <DialogDescription>
            {t(copy('description'), {
              gym: gymName ?? '',
              name: memberName,
              email: invitedEmail,
            })}
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm">
          {t('invitation.claim.signedInAs', { email: user?.email ?? '' })}
        </p>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleSignOut} disabled={claiming}>
            {t('invitation.claim.signOut')}
          </Button>
          <Button onClick={handleClaim} disabled={claiming}>
            {claiming && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('invitation.claim.useCurrentAccount')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
